import { z } from "zod";

export const ranks = [
  "flag",
  "spy",
  "private",
  "sergeant",
  "second-lieutenant",
  "first-lieutenant",
  "captain",
  "major",
  "lieutenant-colonel",
  "colonel",
  "one-star-general",
  "two-star-general",
  "three-star-general",
  "four-star-general",
  "five-star-general",
] as const;

export const rankSchema = z.enum(ranks);
export type Rank = z.infer<typeof rankSchema>;
export type Side = "player" | "opponent";
export type BattleResult = "attacker" | "defender" | "split";

export const rankLabels: Record<Rank, string> = {
  flag: "Flag",
  spy: "Spy",
  private: "Private",
  sergeant: "Sergeant",
  "second-lieutenant": "2nd Lieutenant",
  "first-lieutenant": "1st Lieutenant",
  captain: "Captain",
  major: "Major",
  "lieutenant-colonel": "Lieutenant Colonel",
  colonel: "Colonel",
  "one-star-general": "1-Star General",
  "two-star-general": "2-Star General",
  "three-star-general": "3-Star General",
  "four-star-general": "4-Star General",
  "five-star-general": "5-Star General",
};

export const rankMarks: Record<Rank, string> = {
  flag: "FLG",
  spy: "SPY",
  private: "PVT",
  sergeant: "SGT",
  "second-lieutenant": "2LT",
  "first-lieutenant": "1LT",
  captain: "CPT",
  major: "MAJ",
  "lieutenant-colonel": "LTC",
  colonel: "COL",
  "one-star-general": "1★",
  "two-star-general": "2★",
  "three-star-general": "3★",
  "four-star-general": "4★",
  "five-star-general": "5★",
};

const officerStrength: Partial<Record<Rank, number>> = {
  sergeant: 3,
  "second-lieutenant": 4,
  "first-lieutenant": 5,
  captain: 6,
  major: 7,
  "lieutenant-colonel": 8,
  colonel: 9,
  "one-star-general": 10,
  "two-star-general": 11,
  "three-star-general": 12,
  "four-star-general": 13,
  "five-star-general": 14,
};

export const battleOutcomeSchema = z.object({
  result: z.enum(["attacker", "defender", "split"]),
  attackerRank: rankSchema,
  defenderRank: rankSchema,
  reason: z.string().min(1).max(180),
  capturesFlag: z.boolean(),
});

export type BattleOutcome = z.infer<typeof battleOutcomeSchema>;

const outcome = (
  attackerRank: Rank,
  defenderRank: Rank,
  result: BattleResult,
  reason: string,
): BattleOutcome => ({
  result,
  attackerRank,
  defenderRank,
  reason,
  capturesFlag: defenderRank === "flag" && result === "attacker",
});

export const resolveBattle = (attackerRank: Rank, defenderRank: Rank): BattleOutcome => {
  if (defenderRank === "flag") {
    return outcome(attackerRank, defenderRank, "attacker", "The opposing Flag is captured.");
  }

  if (attackerRank === "flag") {
    return outcome(attackerRank, defenderRank, "defender", "A challenged Flag is eliminated.");
  }

  if (attackerRank === defenderRank) {
    return outcome(attackerRank, defenderRank, "split", "Equal ranks eliminate each other.");
  }

  if (attackerRank === "spy") {
    return defenderRank === "private"
      ? outcome(attackerRank, defenderRank, "defender", "A Private neutralizes a Spy.")
      : outcome(attackerRank, defenderRank, "attacker", "A Spy defeats any officer.");
  }

  if (defenderRank === "spy") {
    return attackerRank === "private"
      ? outcome(attackerRank, defenderRank, "attacker", "A Private neutralizes a Spy.")
      : outcome(attackerRank, defenderRank, "defender", "A Spy defeats any officer.");
  }

  if (attackerRank === "private") {
    return outcome(attackerRank, defenderRank, "defender", "An officer outranks a Private.");
  }

  if (defenderRank === "private") {
    return outcome(attackerRank, defenderRank, "attacker", "An officer outranks a Private.");
  }

  const attackerStrength = officerStrength[attackerRank];
  const defenderStrength = officerStrength[defenderRank];
  if (attackerStrength === undefined || defenderStrength === undefined) {
    throw new Error("Battle ranks could not be compared.");
  }

  return attackerStrength > defenderStrength
    ? outcome(attackerRank, defenderRank, "attacker", "The higher-ranking officer prevails.")
    : outcome(attackerRank, defenderRank, "defender", "The higher-ranking officer prevails.");
};

export const publicPieceSchema = z.object({
  id: z.string().min(1).max(32),
  side: z.enum(["player", "opponent"]),
  row: z.number().int().min(0).max(7),
  column: z.number().int().min(0).max(8),
  rank: rankSchema.nullable(),
  revealed: z.boolean(),
});

export type PublicPiece = z.infer<typeof publicPieceSchema>;

export const scenarioSchema = z.object({
  id: z.literal("breakthrough-at-dawn"),
  title: z.literal("Breakthrough at Dawn"),
  briefing: z.string().min(1).max(240),
  commandLimit: z.number().int().min(1).max(12),
  board: z.object({ rows: z.literal(8), columns: z.literal(9) }),
  pieces: z.array(publicPieceSchema).min(2).max(24),
  synthetic: z.literal(true),
  dataVersion: z.literal("gog-field-manual-v1.0.0"),
});

export type Scenario = z.infer<typeof scenarioSchema>;

export const scenarioResponseSchema = z.object({
  ok: z.literal(true),
  data: scenarioSchema,
  requestId: z.string().uuid(),
});

export const resolveRequestSchema = z.object({
  attackerRank: rankSchema,
  defenderId: z.string().min(1).max(32),
});

export const battleResponseSchema = z.object({
  ok: z.literal(true),
  data: battleOutcomeSchema,
  requestId: z.string().uuid(),
});

export const isAdjacent = (
  from: Pick<PublicPiece, "row" | "column">,
  to: Pick<PublicPiece, "row" | "column">,
) => Math.abs(from.row - to.row) + Math.abs(from.column - to.column) === 1;

export const cellCoordinate = (row: number, column: number) =>
  `${String.fromCharCode(65 + column)}${8 - row}`;
